import { Button, Select, Table, TableColumnsType, TableProps, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { BsPencilSquare } from "react-icons/bs";
import { MonthsDisplay } from "src/entity/common/unit.constant";
import { ChemicalVolume, Filter, OptionFilter } from "src/entity/terminal-marketing/terminal-marketing.entity";
import useNavigationMenu from "src/hook/useNavigationMenu";
import {
  calculateReportVocEmission,
  getChemicalVolume,
  getOptionsChemicalVolume,
  updateChemicalVolume,
} from "src/services/terminal-marketing.service";
import { profileStore } from "src/stores/profile-store";
import { componentPalette } from "src/theme/component-palette";
import { useShallow } from "zustand/react/shallow";
import ManualChemicalVolumeModal from "./ManaulChemicalVolumeModal";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import { canEditTable, canViewTable } from "../../PermissionCheck/PermissionCheck";
import { terminalStore } from "src/stores/terminal-store";
import { companyStore } from "src/stores/company-store";
import { formatNumber } from "src/utils/process-number";
import { generateYearArray } from "src/utils/array";
import { addCenterAlign } from "src/components/AntdCustomPart/TableTitle.tsx/TableTitle";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];
const { Text } = Typography;

const ManualChemicalVolumeTable = () => {
  const precision = 2;
  const months = MonthsDisplay;
  const [companyId, setCompanyId] = useState<string>("");
  const profile = profileStore(useShallow((state) => state.profile));
  const loadingProfile = profileStore(useShallow((state) => state.loading));
  const { permissions } = useNavigationMenu();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [filter, setFilter] = useState<Filters>({});
  const [total, setTotal] = useState<number>(0);
  const [data, setData] = useState<ChemicalVolume[]>([]);
  const [optionYears, setOptionYears] = useState<number[]>([]);
  const currentYear = new Date().getFullYear();
  const [optionsFilter, setOptionFilter] = useState<OptionFilter[]>([]);
  const [showModalChemVolume, setShowModalChemVolume] = useState(false);
  const [initData, setInitData] = useState<ChemicalVolume | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const getChemicalProperty = terminalStore(useShallow((state) => state.getChemicalProperty));
  const chemicalPropertyMaster = terminalStore(useShallow((state) => state.chemicalProperty));

  const getLoadingMethod = terminalStore(useShallow((state) => state.getLoadingMethod));
  const loadingMethodMaster = terminalStore(useShallow((state) => state.loadingMethod));

  const getAreaByCompanyId = companyStore(useShallow((state) => state.getAreaByCompanyId));
  const areaMaster = companyStore(useShallow((state) => state.dataArea));

  const fetchYear = () => {
    const years = generateYearArray();
    setOptionYears(years as number[]);
    setSelectedYear(years[0] as number);
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;
    if (selectedYear) {
      queryParam += `year=${selectedYear}`;
    }
    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchChemicalVolume = async () => {
    try {
      if (companyId && selectedYear) {
        setLoading(true);
        let offset = 0;
        let resultCalOffset = calPagination(current);
        if (resultCalOffset) {
          offset = resultCalOffset;
        }
        const query = createParamFilter();
        const response = await getChemicalVolume(companyId, query);
        if (response.data.code === 200) {
          setLoading(false);
          const data = response.data.data.informations;
          const total = response.data.data.total;
          let information = data.map((el: any, index: number) => {
            return {
              no: (index + offset + 1).toString(),
              ...el,
            };
          });
          setData([...information]);
          setTotal(total);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOption = async () => {
    try {
      if (companyId && selectedYear) {
        const response = await getOptionsChemicalVolume(companyId, selectedYear);
        if (response.data.code === 200) {
          const data = response.data.data.filter;
          setOptionFilter(data);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChemVolume = async (data: ChemicalVolume) => {
    const id = data._id;
    if (!id) {
      messageApi.open({
        type: "error",
        content: CommonError.SOMETHING_WRONG,
      });
      return;
    }

    try {
      data = {
        ...data,
        year: selectedYear,
        companyId: companyId,
      };

      setLoading(true);
      const response = await updateChemicalVolume(id, data);
      if (response?.data.code === 200) {
        setLoading(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.SAVED_SUCCESSFULL,
        });
        fetchChemicalVolume();
        fetchOption();
        setShowModalChemVolume(false);
      }
    } catch (error) {
      console.error(error);
      messageApi.open({
        type: "error",
        content: CommonError.SOMETHING_WRONG,
      });
    } finally {
      setLoading(false);
      processCalculateReport(id?.toString() || "");
    }
  };

  const processCalculateReport = async (id: string) => {
    try {
      setLoading(true);
      await calculateReportVocEmission(id);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = optionsFilter.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        if (key === "area") {
          text = getMasterLabel("area", text);
        } else if (key === "chemicalId") {
          text = getMasterLabel("chemicalId", text);
        } else if (key === "loadingById") {
          text = getMasterLabel("loadingById", text);
        }
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";
    if (type === "area" && areaMaster) {
      const optValue = areaMaster.find((item) => item._id === text);
      return optValue?.areaName;
    } else if (type === "chemicalId" && chemicalPropertyMaster) {
      const optValue = chemicalPropertyMaster.find((item) => item._id === text);
      return optValue?.chemicalName;
    } else if (type === "loadingById" && loadingMethodMaster) {
      const optValue = loadingMethodMaster.find((item) => item._id === text);
      return optValue?.loadingBy;
    } else {
      return text;
    }
  };

  const onOpenModal = (data: ChemicalVolume) => {
    setShowModalChemVolume(true);
    setInitData(data);
  };

  const onTableChange: TableProps<ChemicalVolume>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const intitalData = () => {
    if (profile?.activeCompany?._id) {
      setCompanyId(profile.activeCompany._id);
    }
  };

  const onChangeYear = (value: number) => {
    setSelectedYear(value);
  };

  const resetFilter = () => {
    setFilter({});
  };

  const initMaster = async () => {
    try {
      const query = "";
      setLoading(true);
      await Promise.all([getChemicalProperty(query), getLoadingMethod(query)]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    intitalData();
    initMaster();
    fetchYear();
  }, []);

  useEffect(() => {
    intitalData();
  }, [profile?.activeCompany]);

  useEffect(() => {
    if (loadingProfile === true) {
      setLoading(true);
    }
  }, [loadingProfile]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setSelectedYear(currentYear);
        await Promise.all([getAreaByCompanyId(companyId)]);
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
    setCurrent(1);
  }, [companyId]);

  useEffect(() => {
    resetFilter();
    setCurrent(1);
  }, [selectedYear]);

  useEffect(() => {
    const fetchData = async () => {
      if (companyId && selectedYear) {
        await Promise.all([fetchChemicalVolume(), fetchOption()]);
      }
    };
    fetchData();
  }, [current, filter]);

  const actionColumns: TableColumnsType<ChemicalVolume> = [
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            disabled={!canEditTable(permissions)}
            onClick={() => {
              onOpenModal(record);
            }}
          >
            <BsPencilSquare size={18} color={componentPalette.blueGrey} />
          </Button>
        </div>
      ),
    },
  ];
  const monthColumns: TableColumnsType<ChemicalVolume> = months.map((month) => ({
    title: month.title,
    dataIndex: "manualChemicalVolume",
    render: (value: any) => {
      if (value && typeof value === "object") {
        const key = month.dataIndex;
        if (key === "total") {
          let total = 0;
          months.forEach((itemMonth) => {
            const monthName = itemMonth.dataIndex;
            if (value[monthName] && value[monthName].value) {
              total += parseFloat(value[monthName].value);
            }
          });
          return <Text>{formatNumber(total, precision)}</Text>;
        } else {
          return value[key].value ? <Text>{formatNumber(value[key].value, precision)}</Text> : <Text>0</Text>;
        }
      }
    },
  }));
  const staticColumns: TableColumnsType<ChemicalVolume> = [
    {
      title: "No.",
      dataIndex: "no",
      width: 50,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Chemical Name",
      dataIndex: "chemicalName",
      key: "chemicalId",
      width: 140,
      filters: mapFilter("chemicalId"),
      filteredValue: filter?.chemicalId || null,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Area",
      dataIndex: "area",
      filters: mapFilter("area"),
      filteredValue: filter?.area || null,
      render: (text) => <Text>{getMasterLabel("area", text)}</Text>,
    },
    {
      title: "Loading By",
      dataIndex: "loadingBy",
      key: "loadingById",
      filters: mapFilter("loadingById"),
      filteredValue: filter?.loadingById || null,
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Unit",
      dataIndex: "manualChemicalVolume",
      render: (text) => <Text>{text.unit}</Text>,
    },
  ];
  const columns: TableColumnsType<ChemicalVolume> = [...staticColumns, ...monthColumns, ...actionColumns];

  return (
    <div>
      {contextHolder}
      {initData && (
        <ManualChemicalVolumeModal
          open={showModalChemVolume}
          data={initData}
          areaMaster={areaMaster}
          loading={loading}
          onCloseModal={() => {
            setShowModalChemVolume(false);
          }}
          onSave={(data) => handleSaveChemVolume(data)}
        />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "10px" }}>
        <Select
          value={selectedYear}
          style={{ width: 120 }}
          onChange={onChangeYear}
          options={optionYears.map((year) => ({ label: year, value: year }))}
          loading={loading}
          size="middle"
        />
      </div>
      <Table
        className="w-full whitespace-nowrap"
        scroll={{ x: "max-content" }}
        columns={addCenterAlign(columns)}
        dataSource={canViewTable(permissions) ? data : []}
        onChange={onTableChange}
        loading={loading}
        pagination={{
          showSizeChanger: false,
          size: "small",
          current,
          pageSize,
          total,
        }}
      />
    </div>
  );
};

export default ManualChemicalVolumeTable;
